import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { User, AuthResponse } from '../types';
import { authApi, impersonationApi } from '../services/api';
import { authStorage, setAuthSessionScope, authSessionVersion } from '../services/authStorage';
import { clearAuthSession, SESSION_ENDED, updateSessionState } from '../services/sessionLifecycle';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  establishBankIdSession: (response: AuthResponse, scope?: 'browser' | 'tab') => Promise<User>;
  logout: () => void;
  refreshUser: () => Promise<User | null>;
  activateImpersonation: (token: string) => Promise<User>;
  endImpersonation: () => Promise<User | null>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    const token = authStorage.getItem('accessToken');
    if (!token) {
      setIsLoading(false);
      return null;
    }

    const version = authSessionVersion();
    try {
      const userData = await authApi.me();
      if (version !== authSessionVersion()) return null;
      setUser(userData);
      return userData;
    } catch {
      if (version !== authSessionVersion()) return null;
      authStorage.removeItem('accessToken');
      authStorage.removeItem('refreshToken');
      authStorage.removeItem('token');
      setUser(null);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  useEffect(() => {
    const endSession = () => setUser(null);
    window.addEventListener(SESSION_ENDED, endSession);
    return () => window.removeEventListener(SESSION_ENDED, endSession);
  }, []);

  const handleAuthResponse = (response: AuthResponse, scope: 'browser' | 'tab') => {
    setAuthSessionScope(scope);
    authStorage.setItem('accessToken', response.accessToken);
    authStorage.setItem('refreshToken', response.refreshToken);
    authStorage.removeItem('token');
    authStorage.removeItem('impersonationToken');
    authStorage.removeItem('sessionIdleExpiresAt');
    updateSessionState(response.session);
    setUser(response.user);
  };

  const establishBankIdSession = async (response: AuthResponse, scope: 'browser' | 'tab' = 'browser') => {
    handleAuthResponse(response, scope);
    return (await refreshUser()) ?? response.user;
  };

  const logout = () => {
    const refreshToken = authStorage.getItem('refreshToken');
    if (refreshToken) void authApi.logout(refreshToken).catch(() => {});
    clearAuthSession();
    setUser(null);
  };

  const activateImpersonation = async (token: string) => {
    const response = await impersonationApi.activate(token);
    authStorage.setItem('impersonationToken', response.impersonationToken);
    const impersonatedUser = await refreshUser();
    if (!impersonatedUser?.impersonation) {
      authStorage.removeItem('impersonationToken');
      throw new Error('Personifieringen kunde inte aktiveras.');
    }
    return impersonatedUser;
  };

  const endImpersonation = async () => {
    try {
      if (authStorage.getItem('impersonationToken')) {
        await impersonationApi.end();
      }
    } catch {
      // Always restore the authenticated staff session locally. The API will
      // already have expired or rejected a stale impersonation session.
    } finally {
      authStorage.removeItem('impersonationToken');
    }
    return refreshUser();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        establishBankIdSession,
        logout,
        refreshUser,
        activateImpersonation,
        endImpersonation
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

/** The signed-in context when there is one, without throwing outside a provider. */
export function useOptionalAuth() {
  return useContext(AuthContext);
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
